import { useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { mediaApi } from "../../lib/mediaApi";
import { BLOG_STATUS_LABEL } from "../../lib/blogApi";
import { toast } from "../../components/shared/Toast";

// Quill toolbar — friendly subset for non-technical authors.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "blockquote", "link"];

/* BlogPostForm — markdown editor with side-by-side preview on desktop,
   stacked on mobile. Hero image upload uses same media stub as
   properties. Slug is auto-derived from title but editable. */

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_BYTES = 15 * 1024 * 1024;

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 200);
}

export function BlogPostForm({ initial, onCancel, onSave, onDelete }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    title: initial?.title ?? "",
    slug: initial?.slug ?? "",
    excerpt: initial?.excerpt ?? "",
    body: initial?.body ?? "",
    heroImageUrl: initial?.heroImageUrl ?? "",
    author: initial?.author ?? "Abhi Ghar Team",
    category: initial?.category ?? "",
    status: initial?.status ?? "draft",
    featured: initial?.featured ?? false,
    sortOrder: initial?.sortOrder ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [autoSlug, setAutoSlug] = useState(!initial?.slug);
  const [showPreview, setShowPreview] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleTitleChange = (title) => {
    if (autoSlug) update({ title, slug: slugify(title) });
    else update({ title });
  };

  const wordCount = useMemo(() => {
    return form.body.trim().split(/\s+/).filter(Boolean).length;
  }, [form.body]);
  const readingTime = Math.max(2, Math.ceil(wordCount / 220));

  async function handleHeroUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast("Use JPEG, PNG, WEBP, or GIF");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 15 MB.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const res = await mediaApi.uploadImage(file);
      update({ heroImageUrl: res.url });
      toast("Hero image uploaded");
    } catch (err) {
      toast(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleSave() {
    if (!form.title.trim()) { toast("Title is required"); return; }
    if (!form.body.trim()) { toast("Body is required"); return; }
    if (!form.slug.trim()) { toast("Slug is required"); return; }
    setSaving(true);
    try {
      await onSave({
        title: form.title,
        slug: form.slug,
        excerpt: form.excerpt,
        body: form.body,
        heroImageUrl: form.heroImageUrl,
        author: form.author,
        category: form.category.trim(),
        status: form.status,
        featured: form.featured,
        sortOrder: form.sortOrder,
      });
    } catch {
      // upstream toast handles error
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Header with preview toggle on mobile */}
      <div className="px-4 sm:px-5 py-2 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-500">
          {wordCount} words · {readingTime} min read
        </div>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}  /* deprecated */
          className="lg:hidden text-xs px-3 py-1 border border-slate-300 rounded hover:bg-slate-50"
        >
          {showPreview ? "Edit" : "Preview"}
        </button>
      </div>

      {/* Body — editor + preview side-by-side on lg+, single col below */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-5 space-y-4">
          {/* Top metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Title *">
              <input
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="A clear, compelling title"
                className="form-input"
              />
            </Field>
            <Field label="Slug *">
              <input
                value={form.slug}
                onChange={(e) => { setAutoSlug(false); update({ slug: slugify(e.target.value) }); }}
                placeholder="auto-generated-from-title"
                className="form-input"
              />
            </Field>
          </div>

          <Field label="Excerpt (optional — auto-generated if blank)">
            <textarea
              value={form.excerpt}
              onChange={(e) => update({ excerpt: e.target.value })}
              rows={2}
              placeholder="A 1-2 sentence summary shown in listings"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
            />
          </Field>

          {/* Hero image */}
          <Field label="Hero image">
            {form.heroImageUrl && (
              <div className="mb-2">
                <img
                  src={form.heroImageUrl}
                  alt="Hero preview"
                  className="w-full max-w-md h-32 sm:h-40 object-cover rounded border border-slate-200"
                  onError={(e) => { e.target.style.opacity = 0.3; }}
                />
                <button type="button" onClick={() => update({ heroImageUrl: "" })} className="text-xs text-red-600 mt-1">Remove</button>
              </div>
            )}
            <label className="flex flex-col items-center justify-center gap-2 w-full max-w-md h-28 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-navy/40 cursor-pointer transition-colors">
              <svg className="w-7 h-7 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
              <span className="text-sm font-medium text-navy">{uploading ? "Uploading…" : (form.heroImageUrl ? "Replace image" : "Click to upload image")}</span>
              <span className="text-[11px] text-slate-500">JPEG, PNG, WEBP, GIF · max 15 MB · ideally 1600×900</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleHeroUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </Field>

          {/* Body — WYSIWYG editor (no markdown knowledge needed) */}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Body *</label>
            <div className="blog-quill">
              <ReactQuill
                theme="snow"
                value={form.body}
                onChange={(html) => update({ body: html })}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your post here. Use the toolbar for headings, bold, lists, links…"
              />
            </div>
          </div>

          {/* Publish controls */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Author">
              <input
                value={form.author}
                onChange={(e) => update({ author: e.target.value })}
                placeholder="e.g. Sayeed, Sarika K., Abhi Ghar Team"
                className="form-input"
              />
            </Field>
            <Field label="Category">
              <input
                value={form.category}
                onChange={(e) => update({ category: e.target.value })}
                placeholder="e.g. Market Insights, Buying Guide, Tips"
                className="form-input"
              />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => update({ status: e.target.value })} className="form-input">
                {Object.entries(BLOG_STATUS_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </Field>
            <Field label="Featured (homepage strip)">
              <label className="flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={form.featured} onChange={(e) => update({ featured: e.target.checked })} />
                Show in featured strip
              </label>
            </Field>
            <Field label="Sort order">
              <input type="number" value={form.sortOrder} onChange={(e) => update({ sortOrder: e.target.value })} className="form-input" />
            </Field>
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="px-4 sm:px-5 py-3 border-t border-slate-200 bg-white flex justify-end gap-2">
        {isEdit && onDelete && (
          <button onClick={onDelete} className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded mr-auto">
            Delete
          </button>
        )}
        <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90 disabled:opacity-50">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create post"}
        </button>
      </div>

      <style>{`
        .blog-quill .ql-container { min-height: 18rem; font-size: 0.95rem; border-bottom-left-radius: 0.375rem; border-bottom-right-radius: 0.375rem; }
        .blog-quill .ql-toolbar { border-top-left-radius: 0.375rem; border-top-right-radius: 0.375rem; }
        .blog-quill .ql-editor { min-height: 18rem; }
        .form-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid rgb(203 213 225);
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        .prose h1 { font-size: 1.5rem; font-weight: 700; margin-top: 1.5em; margin-bottom: 0.5em; }
        .prose h2 { font-size: 1.25rem; font-weight: 600; margin-top: 1.5em; margin-bottom: 0.5em; }
        .prose h3 { font-size: 1.1rem; font-weight: 600; margin-top: 1em; margin-bottom: 0.3em; }
        .prose p { margin: 0.8em 0; }
        .prose ul, .prose ol { padding-left: 1.5em; margin: 0.5em 0; }
        .prose li { margin: 0.2em 0; }
        .prose strong { font-weight: 600; }
        .prose em { font-style: italic; }
        .prose code { background: #f1f5f9; padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.9em; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

/* MarkdownPreview — minimal markdown renderer, no external deps.
   Handles h1-h3, bold, italic, code, lists, links, paragraphs. For
   advanced rendering (tables, images, etc.) the customer site uses
   react-markdown. This admin preview is just to spot obvious issues. */
function MarkdownPreview({ source }) {
  if (!source?.trim()) return <p className="text-slate-400 italic">Preview will appear here as you type.</p>;
  const blocks = source.split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (/^### /.test(trimmed)) return <h3 key={i}>{inline(trimmed.replace(/^### /, ""))}</h3>;
        if (/^## /.test(trimmed)) return <h2 key={i}>{inline(trimmed.replace(/^## /, ""))}</h2>;
        if (/^# /.test(trimmed)) return <h1 key={i}>{inline(trimmed.replace(/^# /, ""))}</h1>;
        if (/^[-*] /.test(trimmed)) {
          const items = trimmed.split("\n").filter((l) => /^[-*] /.test(l));
          return <ul key={i}>{items.map((it, j) => <li key={j}>{inline(it.replace(/^[-*] /, ""))}</li>)}</ul>;
        }
        if (/^\d+\. /.test(trimmed)) {
          const items = trimmed.split("\n").filter((l) => /^\d+\. /.test(l));
          return <ol key={i}>{items.map((it, j) => <li key={j}>{inline(it.replace(/^\d+\. /, ""))}</li>)}</ol>;
        }
        return <p key={i}>{inline(trimmed)}</p>;
      })}
    </>
  );

  function inline(text) {
    // Process bold, italic, code, links
    const parts = [];
    let cursor = 0;
    const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let m;
    let key = 0;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > cursor) parts.push(text.slice(cursor, m.index));
      if (m[2]) parts.push(<strong key={key++}>{m[2]}</strong>);
      else if (m[3]) parts.push(<em key={key++}>{m[3]}</em>);
      else if (m[4]) parts.push(<code key={key++}>{m[4]}</code>);
      else if (m[5]) parts.push(<a key={key++} href={m[6]} className="text-navy underline">{m[5]}</a>);
      cursor = m.index + m[0].length;
    }
    if (cursor < text.length) parts.push(text.slice(cursor));
    return parts;
  }
}
